import type { User } from "firebase/auth";
import { syncGmClaimsNow } from "@/auth/sync-gm-claims";
import { readImageFileAsDataUrl } from "@/character/frame";
import { callGetRankBadgeConfig, callSetRankBadgeConfig } from "@/cloud/game-callables";
import { homeIcon } from "@/home/home-icons";
import {
  applyAutoBackgroundRemovalFromDataUrl,
  applyBackgroundRemovalFromDataUrl,
  bindAutoRemoveBlackBgCheckbox,
  bindAutoRemoveWhiteBgCheckbox,
  isAutoRemoveBlackBgEnabled,
  isAutoRemoveWhiteBgEnabled,
} from "@/home/remove-white-bg";
import {
  mountRankBadgeSheetCrop,
  type RankBadgeSheetCropHandle,
} from "@/rank/mount-rank-badge-sheet-crop";
import {
  LEADERBOARD_BOARD_LABELS,
  LEADERBOARD_BOARDS,
  RANK_BADGE_FRAME_HEIGHT,
  RANK_BADGE_FRAME_WIDTH,
  RANK_BADGE_SHEET_HEIGHT,
  RANK_BADGE_SHEET_WIDTH,
  type LeaderboardBoard,
  type RankBadgeConfigBoard,
} from "@/rank/leaderboard-types";

function escapeHtml(text: string): string {
  return text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

type Draft = RankBadgeConfigBoard;

function emptyDraft(board: LeaderboardBoard): Draft {
  return {
    board,
    frameWidth: RANK_BADGE_FRAME_WIDTH,
    frameHeight: RANK_BADGE_FRAME_HEIGHT,
    buff: {},
    updatedAt: 0,
  };
}

function normalizeDraftFrame(draft: Draft): Draft {
  return {
    ...draft,
    frameWidth: RANK_BADGE_FRAME_WIDTH,
    frameHeight: RANK_BADGE_FRAME_HEIGHT,
  };
}

function rankBadgeSheetMetaLabel(): string {
  return `${RANK_BADGE_SHEET_WIDTH}×${RANK_BADGE_SHEET_HEIGHT}px · เฟรม ${RANK_BADGE_FRAME_WIDTH}×${RANK_BADGE_FRAME_HEIGHT}`;
}

function previewImgHtml(dataUrl: string): string {
  return `<img src="${escapeHtml(dataUrl)}" alt="" class="gm-rank-badge-sheet-preview" width="${RANK_BADGE_SHEET_WIDTH}" height="${RANK_BADGE_SHEET_HEIGHT}" />`;
}

const BUFF_FIELDS: { key: keyof Draft["buff"]; label: string }[] = [
  { key: "attack", label: "โจมตี" },
  { key: "defense", label: "ป้องกัน" },
  { key: "dodge", label: "หลบ" },
  { key: "hit", label: "แม่น" },
  { key: "critChance", label: "คริ (0–1)" },
  { key: "hpRegenPerSec", label: "ฟื้น HP/วิ" },
];

function buffSummary(buff: Draft["buff"]): string {
  const parts = BUFF_FIELDS
    .map(({ key, label }) => {
      const v = buff[key];
      if (typeof v !== "number" || !Number.isFinite(v) || v === 0) return null;
      return `${label} ${v}`;
    })
    .filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : "ยังไม่ตั้งบัฟ";
}

function renderListCard(draft: Draft): string {
  const preview = draft.spritesheetDataUrl
    ? `<img class="gm-monster-card-art" src="${escapeHtml(draft.spritesheetDataUrl)}" alt="" width="72" height="36" loading="lazy" />`
    : `<span class="hint gm-rank-badge-placeholder">ไม่มีภาพ</span>`;
  const frameMeta = rankBadgeSheetMetaLabel();
  return `
    <article class="gm-monster-card gm-rank-badge-list-card" role="listitem" data-rank-board="${draft.board}">
      <div class="gm-monster-card-art-wrap gm-rank-badge-art-wrap">
        ${preview}
      </div>
      <div class="gm-monster-card-body">
        <h3 class="gm-monster-card-title">${LEADERBOARD_BOARD_LABELS[draft.board]}</h3>
        <p class="gm-monster-card-stats hint">${escapeHtml(frameMeta)}</p>
        <p class="hint gm-rank-badge-buff-summary">${escapeHtml(buffSummary(draft.buff))}</p>
      </div>
      <footer class="gm-shop-card-toolbar">
        <button type="button" class="gm-icon-tool gm-rank-badge-edit" data-board="${draft.board}" aria-label="แก้ไข" title="แก้ไข">${homeIcon("decorate")}</button>
      </footer>
    </article>`;
}

function renderListBody(drafts: Draft[]): string {
  return `<div class="gm-monster-card-grid" role="list">${drafts.map(renderListCard).join("")}</div>`;
}

function renderEditForm(draft: Draft): string {
  const buffInputs = BUFF_FIELDS
    .map(
      ({ key, label }) => `
      <label class="field gm-monster-field">
        <span class="field-label">${label}</span>
        <input type="number" class="input" step="any" id="gm-rank-buff-${key}" value="${draft.buff[key] ?? ""}" />
      </label>`,
    )
    .join("");
  const preview = draft.spritesheetDataUrl
    ? previewImgHtml(draft.spritesheetDataUrl)
    : `<span class="hint">ยังไม่มีภาพ</span>`;

  return `
    <form class="gm-monster-form" id="gm-rank-badge-edit-form">
      <section class="gm-monster-form-section" aria-labelledby="gm-rank-sec-image">
        <h3 class="gm-monster-form-section-title" id="gm-rank-sec-image">สไปรต์ป้าย</h3>
        <p class="hint">ผลลัพธ์ <strong>${RANK_BADGE_SHEET_WIDTH}×${RANK_BADGE_SHEET_HEIGHT}px</strong> · 2 เฟรม (${RANK_BADGE_FRAME_WIDTH}×${RANK_BADGE_FRAME_HEIGHT}) · อัปโหลดแล้วลากครอป (อัตราส่วนคงที่)</p>
        <div class="gm-monster-upload-zone">
          <label class="gm-monster-upload-label">
            <span class="gm-monster-upload-icon" aria-hidden="true">↑</span>
            <span class="gm-monster-upload-text">เลือกรูปแล้วครอป</span>
            <input type="file" id="gm-rank-badge-image-file" accept="image/png,image/jpeg,image/webp" hidden />
          </label>
          <div id="gm-rank-badge-crop-host" class="gm-rank-badge-crop-host" hidden></div>
          <div class="gm-rank-badge-preview-wrap" id="gm-rank-badge-preview-wrap">
            <div class="gm-monster-preview-slot gm-rank-badge-preview-slot" id="gm-rank-badge-preview-slot">${preview}</div>
            <button type="button" class="btn btn-ghost btn-sm" id="gm-rank-badge-recrop" ${draft.spritesheetDataUrl ? "" : "hidden"}>ครอปใหม่</button>
          </div>
        </div>
        <div class="gm-rank-bg-tools">
          <label class="home-remove-white-bg" title="ตัดพื้นขาวเมื่ออัปโหลด">
            <input type="checkbox" id="gm-rank-remove-white-bg" />
            <span>ลบพื้นหลังขาว (อัตโนมัติเมื่ออัปโหลด)</span>
          </label>
          <label class="home-remove-white-bg" title="ตัดพื้นดำเมื่ออัปโหลด">
            <input type="checkbox" id="gm-rank-remove-black-bg" />
            <span>ลบพื้นหลังดำ (อัตโนมัติเมื่ออัปโหลด)</span>
          </label>
          <button type="button" class="btn btn-ghost btn-sm" id="gm-rank-apply-remove-bg" ${draft.spritesheetDataUrl ? "" : "disabled"}>
            ลบพื้นหลังจากภาพนี้
          </button>
        </div>
      </section>
      <section class="gm-monster-form-section" aria-labelledby="gm-rank-sec-buff">
        <h3 class="gm-monster-form-section-title" id="gm-rank-sec-buff">บัฟอันดับ 1–10 (บอร์ดนี้)</h3>
        <div class="gm-rank-buff-grid">${buffInputs}</div>
      </section>
      <p class="hint gm-monster-form-status" id="gm-rank-badge-modal-status" role="status"></p>
      <div class="gm-monster-modal-foot">
        <button type="button" class="btn btn-ghost" id="gm-rank-badge-cancel">ยกเลิก</button>
        <button type="submit" class="btn btn-primary">บันทึก</button>
      </div>
    </form>`;
}

export function renderGmRankBadgesSectionHtml(): string {
  return `
    <section class="gm-section" id="gm-rank-badges-section">
      <p class="hint gm-settings-lead">สไปรต์ ${RANK_BADGE_SHEET_WIDTH}×${RANK_BADGE_SHEET_HEIGHT}px · 2 เฟรม · บัฟอันดับ 1–10 · แสดงทุกบอร์ดที่ติด top 10</p>
      <div class="gm-monsters-page gm-rank-badges-page">
        <header class="gm-monsters-toolbar">
          <div class="gm-monsters-toolbar-text">
            <h2 class="gm-monsters-toolbar-title">ป้ายแรงค์</h2>
            <p class="hint">${LEADERBOARD_BOARDS.length} บอร์ด</p>
          </div>
        </header>
        <div id="gm-rank-badges-host"></div>
        <p class="hint gm-monster-toast" id="gm-rank-badges-status" role="status"></p>
      </div>
    </section>

    <div id="gm-rank-badge-modal" class="gm-monster-modal" hidden>
      <button type="button" class="gm-monster-modal-backdrop" id="gm-rank-badge-modal-backdrop" aria-label="ปิด"></button>
      <div class="gm-monster-modal-dialog" role="dialog" aria-modal="true" aria-labelledby="gm-rank-badge-modal-title">
        <header class="gm-monster-modal-head">
          <h2 class="gm-monster-modal-title" id="gm-rank-badge-modal-title">แก้ไขป้ายแรงค์</h2>
          <button type="button" class="btn btn-ghost btn-sm gm-monster-modal-close" id="gm-rank-badge-modal-close" aria-label="ปิด">✕</button>
        </header>
        <div class="gm-monster-modal-body" id="gm-rank-badge-modal-body"></div>
      </div>
    </div>
  `;
}

export async function prepareGmRankBadgesSection(): Promise<Draft[]> {
  try {
    const res = await callGetRankBadgeConfig();
    const map = new Map<LeaderboardBoard, Draft>();
    for (const b of res.boards ?? []) map.set(b.board, b);
    return LEADERBOARD_BOARDS.map((board) =>
      normalizeDraftFrame(map.get(board) ?? emptyDraft(board)),
    );
  } catch {
    return LEADERBOARD_BOARDS.map((board) => emptyDraft(board));
  }
}

function readFormIntoDraft(root: HTMLElement, base: Draft): Draft {
  const next: Draft = {
    ...normalizeDraftFrame(base),
    buff: { ...base.buff },
    updatedAt: Date.now(),
  };
  for (const { key } of BUFF_FIELDS) {
    const inp = root.querySelector<HTMLInputElement>(`#gm-rank-buff-${key}`);
    const v = inp?.value.trim();
    if (!v) {
      delete next.buff[key];
    } else {
      const n = Number(v);
      if (Number.isFinite(n)) next.buff[key] = n;
    }
  }
  return next;
}

function isFunctionsPermissionDenied(err: unknown): boolean {
  return (
    typeof err === "object" &&
    err !== null &&
    "code" in err &&
    (err as { code: string }).code === "functions/permission-denied"
  );
}

export function bindGmRankBadgesSection(root: HTMLElement, user: User, drafts: Draft[]): () => void {
  let editingBoard: LeaderboardBoard | null = null;
  let modalDraft: Draft | null = null;
  let modalImageUrl: string | null = null;
  let modalSourceUrl: string | null = null;
  let cropHandle: RankBadgeSheetCropHandle | null = null;

  const host = root.querySelector<HTMLElement>("#gm-rank-badges-host");
  if (!host) return () => {};

  const setToast = (msg: string) => {
    const el = root.querySelector<HTMLElement>("#gm-rank-badges-status");
    if (el) el.textContent = msg;
  };

  const setModalStatus = (msg: string) => {
    const el = root.querySelector<HTMLElement>("#gm-rank-badge-modal-status");
    if (el) el.textContent = msg;
  };

  const refreshList = () => {
    host.innerHTML = renderListBody(drafts);
  };

  const destroyCrop = () => {
    cropHandle?.destroy();
    cropHandle = null;
  };

  const setCropVisible = (visible: boolean) => {
    const cropHost = root.querySelector<HTMLElement>("#gm-rank-badge-crop-host");
    const previewWrap = root.querySelector<HTMLElement>("#gm-rank-badge-preview-wrap");
    if (cropHost) cropHost.hidden = !visible;
    if (previewWrap) previewWrap.hidden = visible;
  };

  const setRecropVisible = (visible: boolean) => {
    const btn = root.querySelector<HTMLButtonElement>("#gm-rank-badge-recrop");
    if (btn) btn.hidden = !visible;
  };

  const closeModal = () => {
    destroyCrop();
    const modal = root.querySelector<HTMLElement>("#gm-rank-badge-modal");
    if (modal) modal.hidden = true;
    document.body.classList.remove("gm-monster-modal-open");
    editingBoard = null;
    modalDraft = null;
    modalImageUrl = null;
    modalSourceUrl = null;
  };

  const setPreviewUrl = (url: string | null) => {
    const slot = root.querySelector<HTMLElement>("#gm-rank-badge-preview-slot");
    const applyBtn = root.querySelector<HTMLButtonElement>("#gm-rank-apply-remove-bg");
    if (!slot) return;
    if (!url) {
      slot.innerHTML = `<span class="hint">ยังไม่มีภาพ · อัปโหลดแล้วครอป</span>`;
      if (applyBtn) applyBtn.disabled = true;
      setRecropVisible(false);
      return;
    }
    slot.innerHTML = previewImgHtml(url);
    if (applyBtn) applyBtn.disabled = false;
    setRecropVisible(Boolean(modalSourceUrl));
  };

  const startCrop = (sourceUrl: string) => {
    modalSourceUrl = sourceUrl;
    destroyCrop();
    setCropVisible(true);
    const cropHost = root.querySelector<HTMLElement>("#gm-rank-badge-crop-host");
    if (!cropHost) return;
    cropHandle = mountRankBadgeSheetCrop(cropHost, sourceUrl, {
      onApplied: (cropped) => {
        modalImageUrl = cropped;
        if (modalDraft) modalDraft.spritesheetDataUrl = cropped;
        destroyCrop();
        setCropVisible(false);
        setPreviewUrl(cropped);
        setModalStatus("");
      },
      onCancel: () => {
        destroyCrop();
        setCropVisible(false);
        if (modalImageUrl) {
          setPreviewUrl(modalImageUrl);
        } else {
          setPreviewUrl(null);
        }
      },
      exportBg: () => ({
        removeWhiteBg: isAutoRemoveWhiteBgEnabled(),
        removeBlackBg: isAutoRemoveBlackBgEnabled(),
      }),
    });
  };

  const bindModalBgTools = () => {
    const white = root.querySelector<HTMLInputElement>("#gm-rank-remove-white-bg");
    const black = root.querySelector<HTMLInputElement>("#gm-rank-remove-black-bg");
    if (white) bindAutoRemoveWhiteBgCheckbox(white);
    if (black) bindAutoRemoveBlackBgCheckbox(black);
  };

  const applyBgRemovalToModalImage = async (autoOnly: boolean): Promise<void> => {
    if (!modalImageUrl) return;
    const removeWhite = isAutoRemoveWhiteBgEnabled();
    const removeBlack = isAutoRemoveBlackBgEnabled();
    if (autoOnly && !removeWhite && !removeBlack) return;
    if (!autoOnly && !removeWhite && !removeBlack) {
      setModalStatus("เลือกลบพื้นขาวหรือดำก่อน");
      return;
    }
    try {
      const next = await applyBackgroundRemovalFromDataUrl(modalImageUrl, {
        removeWhite,
        removeBlack,
      });
      modalImageUrl = next;
      if (modalDraft) modalDraft.spritesheetDataUrl = next;
      setPreviewUrl(next);
      modalSourceUrl = next;
      setModalStatus("ลบพื้นหลังแล้ว");
    } catch {
      setModalStatus("ลบพื้นหลังไม่สำเร็จ");
    }
  };

  const openModal = (board: LeaderboardBoard) => {
    const draft = drafts.find((d) => d.board === board);
    if (!draft) return;
    editingBoard = board;
    modalDraft = normalizeDraftFrame({ ...draft, buff: { ...draft.buff } });
    modalImageUrl = draft.spritesheetDataUrl ?? null;
    modalSourceUrl = draft.spritesheetDataUrl ?? null;
    const modal = root.querySelector<HTMLElement>("#gm-rank-badge-modal");
    const body = root.querySelector<HTMLElement>("#gm-rank-badge-modal-body");
    const title = root.querySelector<HTMLElement>("#gm-rank-badge-modal-title");
    if (title) {
      title.textContent = `แก้ไขป้าย · ${LEADERBOARD_BOARD_LABELS[board]}`;
    }
    if (body && modalDraft) body.innerHTML = renderEditForm(modalDraft);
    bindModalBgTools();
    setCropVisible(false);
    if (modal) modal.hidden = false;
    document.body.classList.add("gm-monster-modal-open");
    setModalStatus("");
  };

  refreshList();

  const onRootClick = (event: Event) => {
    const target = event.target as HTMLElement;
    const editBtn = target.closest<HTMLButtonElement>(".gm-rank-badge-edit");
    if (editBtn?.dataset.board) {
      openModal(editBtn.dataset.board as LeaderboardBoard);
      return;
    }
    const card = target.closest<HTMLElement>(".gm-rank-badge-list-card");
    if (card?.dataset.rankBoard && !target.closest(".gm-rank-badge-edit")) {
      openModal(card.dataset.rankBoard as LeaderboardBoard);
      return;
    }
    if (target.id === "gm-rank-apply-remove-bg" || target.closest("#gm-rank-apply-remove-bg")) {
      void applyBgRemovalToModalImage(false);
      return;
    }
    if (target.id === "gm-rank-badge-recrop" || target.closest("#gm-rank-badge-recrop")) {
      if (modalSourceUrl) startCrop(modalSourceUrl);
      return;
    }
    if (
      target.id === "gm-rank-badge-modal-backdrop" ||
      target.id === "gm-rank-badge-modal-close" ||
      target.closest("#gm-rank-badge-cancel")
    ) {
      closeModal();
    }
  };

  const onRootChange = (event: Event) => {
    const input = event.target as HTMLInputElement;
    if (input.id !== "gm-rank-badge-image-file" || !modalDraft) return;
    const file = input.files?.[0];
    if (!file) return;
    void (async () => {
      try {
        let dataUrl = await readImageFileAsDataUrl(file);
        dataUrl = await applyAutoBackgroundRemovalFromDataUrl(dataUrl);
        modalImageUrl = null;
        if (modalDraft) modalDraft.spritesheetDataUrl = undefined;
        setModalStatus("");
        startCrop(dataUrl);
      } catch {
        setModalStatus("อัปโหลดรูปไม่สำเร็จ");
      }
    })();
  };

  const onSubmit = (event: Event) => {
    const form = (event.target as HTMLElement).closest("form");
    if (form?.id !== "gm-rank-badge-edit-form") return;
    event.preventDefault();
    if (!editingBoard || !modalDraft) return;
    void (async () => {
      const merged = normalizeDraftFrame(readFormIntoDraft(root, modalDraft));
      if (modalImageUrl) merged.spritesheetDataUrl = modalImageUrl;
      const idx = drafts.findIndex((d) => d.board === editingBoard);
      if (idx < 0) return;
      drafts[idx] = merged;
      try {
        await callSetRankBadgeConfig(drafts);
        setToast("บันทึกแล้ว");
        refreshList();
        closeModal();
      } catch (err) {
        if (isFunctionsPermissionDenied(err)) {
          await syncGmClaimsNow(user);
          try {
            await callSetRankBadgeConfig(drafts);
            setToast("บันทึกแล้ว");
            refreshList();
            closeModal();
            return;
          } catch {
            setModalStatus(
              "ต้องเป็น owner ฝั่ง server — ตั้ง GM_OWNER_EMAIL ใน Functions หรือ seed Firestore config/gmAccess แล้วซิงค์ claims (ภาพรวม)",
            );
            return;
          }
        }
        setModalStatus(err instanceof Error ? err.message : "บันทึกไม่สำเร็จ");
      }
    })();
  };

  root.addEventListener("click", onRootClick);
  root.addEventListener("change", onRootChange);
  root.addEventListener("submit", onSubmit);

  return () => {
    root.removeEventListener("click", onRootClick);
    root.removeEventListener("change", onRootChange);
    root.removeEventListener("submit", onSubmit);
    closeModal();
  };
}
